import { api, type Page } from './client'

/** TASK-0026 / ADR-007: the lotto's own 3-value lifecycle — a distinct concept from
 * ContractStatus (see api/contracts.ts's CONTRACT_STATUS_LABELS). */
export type BatchStatus = 'InAttesaApprovazione' | 'Approvato' | 'Rifiutato'

/** REQ-003-001: same shared label/color convention as api/contracts.ts's
 * CONTRACT_STATUS_LABELS/CONTRACT_STATUS_COLORS, so BatchesPage.tsx and any future consumer of
 * {@link BatchStatus} can't drift on labels/colors the way ContractsPage.tsx used to (TASK-0025 /
 * ADR-006). */
export const BATCH_STATUS_LABELS: Record<BatchStatus, string> = {
  InAttesaApprovazione: 'In attesa di approvazione',
  Approvato: 'Approvato',
  Rifiutato: 'Rifiutato',
}

/** Bootstrap badge color per status — same 3-value vocabulary as {@link BATCH_STATUS_LABELS}. */
export const BATCH_STATUS_COLORS: Record<BatchStatus, string> = {
  InAttesaApprovazione: 'warning',
  Approvato: 'success',
  Rifiutato: 'danger',
}

/** F-078 (REQ-008-003): priorità del lotto, solo un'etichetta di lavorazione. */
export type BatchPriority = 'Normale' | 'Alta' | 'Urgente'

export const BATCH_PRIORITY_LABELS: Record<BatchPriority, string> = {
  Normale: 'Normale',
  Alta: 'Alta',
  Urgente: 'Urgente',
}

/** I criteri con cui un lotto si porziona (spec §8). Tutti facoltativi. */
export interface BatchCriteria {
  departmentId?: number | null
  productId?: number | null
  customerSegment?: string | null
  /** Canale: contracts.contract_type. */
  contractType?: string | null
  periodFrom?: string | null
  periodTo?: string | null
  priority?: BatchPriority
  contestId?: number | null
  contestLabel?: string | null
  maxItems?: number | null
  /** F-154 (REQ-008-002): al massimo 50 agenti; escluso con managerUserId. */
  agentIds?: number[] | null
  /** F-154 (REQ-008-002): squadra = il responsabile più i suoi subordinati, espansa quando si aggiungono le pratiche. */
  managerUserId?: number | null
  /** R3-08: campo dinamico della pratica con la convenzione/accordo, e valore cercato. */
  conventionFieldCode?: string | null
  conventionValue?: string | null
  /** R3-08: intervallo di valore economico (ExpectedAmount); null senza CanSeeRealAmounts. */
  valueMin?: number | null
  valueMax?: number | null
}

export interface Batch {
  id: number
  batchCode: string
  operatorId?: number
  status: BatchStatus
  reason?: string
  createdByUserId?: number
  createdAt: string
  /** Null while the batch is still being assembled (items can be added/removed) — set once the
   * batch is sent for approval. See Batch.cs's remarks for why this, not a 4th BatchStatus value,
   * is the "bozza lotto" signal. */
  submittedAt?: string
  approvedByUserId?: number
  approvedAt?: string
  rejectedByUserId?: number
  rejectedAt?: string
  rejectionReason?: string
  // F-078: criteri, regola di origine, ultima revoca.
  departmentId?: number | null
  productId?: number | null
  customerSegment?: string | null
  contractType?: string | null
  periodFrom?: string | null
  periodTo?: string | null
  priority?: BatchPriority
  contestId?: number | null
  contestLabel?: string | null
  maxItems?: number | null
  /** F-154: agenti ammessi nel lotto (null = nessun vincolo). */
  agentIds?: number[] | null
  /** F-154: responsabile della squadra ammessa (null = nessun vincolo). */
  managerUserId?: number | null
  conventionFieldCode?: string | null
  conventionValue?: string | null
  valueMin?: number | null
  valueMax?: number | null
  createdByRuleId?: number | null
  revokedByUserId?: number | null
  revokedAt?: string | null
  revocationReason?: string | null
}

export interface BatchItem {
  id: number
  contractId: number
  createdAt: string
}

export type BatchRuleTargetMode = 'NuovoLotto' | 'LottoEsistente'

/** F-078: una regola di assegnazione automatica delle pratiche Verificate ai lotti. */
export interface BatchRule {
  id: number
  name: string
  /** Ordine di valutazione: il più basso per primo. */
  priority: number
  isActive: boolean
  operatorId?: number | null
  departmentId?: number | null
  productId?: number | null
  customerSegment?: string | null
  contractType?: string | null
  periodFrom?: string | null
  periodTo?: string | null
  batchPriority: BatchPriority
  contestId?: number | null
  contestLabel?: string | null
  maxItems?: number | null
  /** Elenco esplicito di agenti; escluso con managerUserId. */
  agentIds?: number[] | null
  /** Squadra: il responsabile e tutti i suoi subordinati. */
  managerUserId?: number | null
  conventionFieldCode?: string | null
  conventionValue?: string | null
  valueMin?: number | null
  valueMax?: number | null
  verifiedFrom?: string | null
  verifiedTo?: string | null
  targetMode: BatchRuleTargetMode
  targetBatchId?: number | null
  reason?: string | null
}

/** Il corpo di creazione/modifica di una regola (sostituzione completa). */
export type BatchRuleInput = Omit<BatchRule, 'id'>

export interface BatchRuleApplyBatch {
  slot: number
  ruleId: number
  ruleName: string
  operatorId: number | null
  existingBatchId: number | null
  /** Null in anteprima: nulla viene creato. */
  batchId: number | null
  batchCode: string | null
  contractIds: number[]
}

export interface BatchRuleApplyDiscard {
  contractId: number
  referenceCode: string
  reason: string
  ruleId: number | null
}

export interface BatchRuleApplyResult {
  dryRun: boolean
  candidates: number
  assigned: number
  createdBatches: number
  batches: BatchRuleApplyBatch[]
  discarded: BatchRuleApplyDiscard[]
}

export type BatchAnomalyKind =
  | 'CampoMancante'
  | 'DocumentoMancante'
  | 'DocumentoScaduto'
  | 'IbanNonValido'
  | 'IncongruenzaDati'
  | 'Duplicato'
  | 'ProvvigioneAnomala'

export const BATCH_ANOMALY_LABELS: Record<BatchAnomalyKind, string> = {
  CampoMancante: 'Campo mancante',
  DocumentoMancante: 'Documento mancante',
  DocumentoScaduto: 'Documento scaduto',
  IbanNonValido: 'IBAN non valido',
  IncongruenzaDati: 'Dati incongruenti',
  Duplicato: 'Duplicato',
  ProvvigioneAnomala: 'Provvigione anomala',
}

export interface BatchCheckRow {
  contractId: number
  referenceCode: string
  agentId: number | null
  color: 'Rosso' | 'Giallo' | 'Verde'
  scorePercent: number
  anomalies: { kind: BatchAnomalyKind; severity: 'Bloccante' | 'Avviso'; label: string }[]
}

/** I controlli di un lotto: si mostrano, non bloccano. Nessun importo. */
export interface BatchChecks {
  batchId: number
  batchCode: string
  itemCount: number
  rosso: number
  giallo: number
  verde: number
  itemsWithAnomalies: number
  anomalyCount: number
  blockingAnomalyCount: number
  byKind: Partial<Record<BatchAnomalyKind, number>>
  agentIds: number[]
  items: BatchCheckRow[]
}

/** F-094 (REQ-277-001): ordinamenti accettati dal server per l'elenco dei lotti. */
export type BatchSort = 'created_desc' | 'created_asc' | 'status'

export const batchesApi = {
  list: (p: { status?: string | undefined; page?: number; pageSize?: number; sort?: BatchSort | undefined } = {}) => {
    const q = new URLSearchParams()
    if (p.status) q.set('status', p.status)
    if (p.sort) q.set('sort', p.sort)
    q.set('page', String(p.page ?? 1))
    q.set('pageSize', String(p.pageSize ?? 25))
    return api<Page<Batch>>(`/api/batches?${q}`)
  },

  get: (id: number) => api<{ batch: Batch; items: BatchItem[] }>(`/api/batches/${id}`),

  create: (operatorId: number | undefined, reason?: string, criteria: BatchCriteria = {}) =>
    api<{ id: number; batchCode: string }>('/api/batches', {
      method: 'POST',
      body: JSON.stringify({ operatorId, reason, ...criteria }),
    }),

  /** operatorId is resolved by the caller (Contract.ProductId -> Product.OperatorId, via Catalog
   * data the frontend already has) as a convenience/UX hint — the backend independently re-verifies
   * it server-side (IProductOperatorReader) and refuses the call if it doesn't match. See
   * ContractsPage.tsx's handleAssignToBatch and BatchesController.cs's remarks. */
  assignItems: (id: number, contractIds: number[], operatorId: number) =>
    api<{ added: number }>(`/api/batches/${id}/items`, {
      method: 'POST',
      body: JSON.stringify({ contractIds, operatorId }),
    }),

  removeItem: (id: number, contractId: number) =>
    api<void>(`/api/batches/${id}/items/${contractId}`, { method: 'DELETE' }),

  submit: (id: number) => api<void>(`/api/batches/${id}/submit`, { method: 'POST' }),

  approve: (id: number) => api<void>(`/api/batches/${id}/approve`, { method: 'POST' }),

  reject: (id: number, reason: string) =>
    api<void>(`/api/batches/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /** F-078 (REQ-009-005): revoca di un lotto approvato prima della fase operatore (solo Manager). */
  revoke: (id: number, reason: string) =>
    api<void>(`/api/batches/${id}/revoke`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /** F-078: controlli (anomalie e readiness) delle voci del lotto. */
  checks: (id: number) => api<BatchChecks>(`/api/batches/${id}/checks`),

  listRules: () => api<BatchRule[]>('/api/batches/rules'),

  createRule: (rule: BatchRuleInput) =>
    api<BatchRule>('/api/batches/rules', { method: 'POST', body: JSON.stringify(rule) }),

  updateRule: (id: number, rule: BatchRuleInput) =>
    api<BatchRule>(`/api/batches/rules/${id}`, { method: 'PUT', body: JSON.stringify(rule) }),

  /** dryRun=true (anteprima, nessuna scrittura) o false (esegue le assegnazioni). Non sottomette i lotti. */
  applyRules: (dryRun: boolean) =>
    api<BatchRuleApplyResult>(`/api/batches/rules/apply?dryRun=${dryRun ? 'true' : 'false'}`, {
      method: 'POST',
    }),
}
